import { Controller } from "@hotwired/stimulus"
import { chartJsFor, injectCountLine, scheduleWhenReady } from "../../helpers/chartkick_chart.js"

const COMPRESS_LABEL_LOT_LIMIT = 8

export default class extends Controller {
  static targets = ["backButton", "focusedPeriod"]
  static outlets = ["sanitation-reports--metric-switcher"]

  static values = {
    chartId: String,
    kind: String,
    periods: Object,
    clickHint: String,
    presenceLabel: String,
    countLabel: String,
    presenceBarColor: {type: String, default: "#22c55e"},
  }

  connect() {
    this.focused = false
    this.focusedLabel = null
    this.patchedChart = null
    this.timeouts = []
    this.boundOnCanvasClick = (event) => this.#onCanvasClick(event)
    this.boundOnCanvasMove = (event) => this.#onCanvasMove(event)
    this.boundOnCanvasLeave = () => this.#onCanvasLeave()
    this.element.addEventListener("click", this.boundOnCanvasClick)
    this.element.addEventListener("mousemove", this.boundOnCanvasMove)
    this.element.addEventListener("mouseleave", this.boundOnCanvasLeave)
    scheduleWhenReady(this.chartIdValue, () => this.#onChartReady(), this.timeouts)
  }

  disconnect() {
    this.timeouts.forEach((id) => clearTimeout(id))
    if (this.moveFrame) cancelAnimationFrame(this.moveFrame)
    this.element.removeEventListener("click", this.boundOnCanvasClick)
    this.element.removeEventListener("mousemove", this.boundOnCanvasMove)
    this.element.removeEventListener("mouseleave", this.boundOnCanvasLeave)
  }

  restore(event) {
    event?.preventDefault()
    if (!this.focused) return

    const chart = this.#chartJs()
    if (!chart || !this.originalLabels) return

    this.focused = false
    this.focusedLabel = null
    this.#hideFocusChrome()

    if (this.#kindHandlers().restoreMeasurement && this.#restoreMeasurementPeriodData()) {
      this.#forgetSnapshot()
      this.#patchLiveChart()
      return
    }

    chart.data.labels = this.originalLabels.slice()
    chart.data.datasets = this.originalDatasets.slice()
    this.#restoreXTicks(chart)
    chart.update()
  }

  metricChanged() {
    if (this.focused && this.focusedLabel) {
      this.#focusLabel(this.focusedLabel)
      return
    }

    this.#forgetSnapshot()
    this.#patchLiveChart()
  }

  #onChartReady() {
    return Boolean(this.#chartJs()?.canvas)
  }

  #patchLiveChart() {
    this.patchedChart = null
    this.#onChartReady()
  }

  #forgetSnapshot() {
    this.originalLabels = null
    this.originalDatasets = null
    this.originalXTicks = null
  }

  #focusLabel(label) {
    const lots = this.periodsValue[label]
    if (!lots?.length) return

    const chart = this.#chartJs()
    if (!chart) return

    this.#ensureOriginalSnapshot(chart)
    this.focused = true
    this.focusedLabel = label
    this.#showFocusChrome(label)

    const kind = this.#kindHandlers()
    const displayLots = kind.rankLots(lots)
    chart.data.labels = displayLots.map((lot) => lot.name)
    chart.data.datasets = kind.datasets(displayLots)
    this.#applyCompressedTicks(chart, displayLots.length)
    chart.update()
    kind.afterFocus?.(chart, displayLots)
  }

  #ensureOriginalSnapshot(chart) {
    if (this.originalLabels) return

    this.originalLabels = chart.data.labels.slice()
    this.originalDatasets = chart.data.datasets.slice()
    const ticks = chart.options.scales?.x?.ticks
    this.originalXTicks = ticks
      ? {
        autoSkip: ticks.autoSkip,
        maxRotation: ticks.maxRotation,
        minRotation: ticks.minRotation,
      }
      : null
  }

  #onCanvasClick(event) {
    const chart = this.#chartJs()
    if (!chart || this.focused) return
    if (event.target !== chart.canvas) return
    if (typeof chart.getElementsAtEventForMode !== "function") return

    const elements = chart.getElementsAtEventForMode(event, "nearest", {intersect: this.#hitTestIntersect()}, true)
    if (!elements?.length) return

    const label = chart.data.labels[elements[0].index]
    if (!this.#isClickable(label)) return

    this.#focusLabel(label)
  }

  #onCanvasMove(event) {
    this.pendingMoveEvent = event
    if (this.moveFrame) return

    this.moveFrame = requestAnimationFrame(() => {
      this.moveFrame = null
      const pending = this.pendingMoveEvent
      this.pendingMoveEvent = null
      if (pending) this.#hitTestMove(pending)
    })
  }

  #hitTestMove(event) {
    const chart = this.#chartJs()
    const canvas = chart?.canvas
    if (!canvas || typeof chart.getElementsAtEventForMode !== "function") return

    if (this.focused || event.target !== canvas) {
      this.#resetCanvasCursor(canvas)
      return
    }

    const elements = chart.getElementsAtEventForMode(event, "nearest", {intersect: this.#hitTestIntersect()}, true)
    if (!elements?.length) {
      this.#resetCanvasCursor(canvas)
      return
    }

    const label = chart.data.labels[elements[0].index]
    if (this.#isClickable(label)) {
      canvas.style.cursor = "pointer"
      canvas.title = this.clickHintValue
      return
    }

    this.#resetCanvasCursor(canvas)
  }

  #onCanvasLeave() {
    const canvas = this.#chartJs()?.canvas
    if (canvas) this.#resetCanvasCursor(canvas)
  }

  #resetCanvasCursor(canvas) {
    canvas.style.cursor = "default"
    canvas.title = ""
  }

  #tooltipFooter(items) {
    if (this.focused) return ""

    const label = items?.[0]?.label
    if (!this.#isClickable(label)) return ""

    return this.clickHintValue
  }

  #patchTooltipFooter(chart) {
    const callbacks = chart.options.plugins?.tooltip?.callbacks
    if (!callbacks) return

    callbacks.footer = (items) => this.#tooltipFooter(items)
  }

  #applyCompressedTicks(chart, lotCount) {
    const ticks = chart.options.scales?.x?.ticks
    if (!ticks) return
    if (lotCount <= COMPRESS_LABEL_LOT_LIMIT) {
      this.#restoreXTicks(chart)
      return
    }

    ticks.autoSkip = false
    ticks.maxRotation = 60
    ticks.minRotation = 45
  }

  #restoreXTicks(chart) {
    const ticks = chart.options.scales?.x?.ticks
    if (!ticks || !this.originalXTicks) return

    ticks.autoSkip = this.originalXTicks.autoSkip
    ticks.maxRotation = this.originalXTicks.maxRotation
    ticks.minRotation = this.originalXTicks.minRotation
  }

  #kindHandlers() {
    const handlers = {
      presence: {
        rankLots: (lots) => lots,
        datasets: (lots) => this.#presenceDatasets(lots),
        hitTestIntersect: true,
        afterFocus: (chart, lots) => {
          injectCountLine(chart, {
            label: this.countLabelValue,
            values: lots.map((lot) => lot.count || 0),
          })
        },
      },
      measurement: {
        rankLots: (lots) => this.#rankedMeasurementLots(lots, this.#currentMetric()),
        datasets: (lots) => this.#measurementDatasets(lots),
        hitTestIntersect: false,
        restoreMeasurement: true,
      },
      distribution: {
        rankLots: (lots) => lots,
        datasets: (lots) => this.#distributionDatasets(lots),
        hitTestIntersect: true,
      },
    }

    return handlers[this.kindValue] || handlers.presence
  }

  #rankedMeasurementLots(lots, metric) {
    return lots.slice().sort((left, right) => {
      const delta = (right[metric] ?? 0) - (left[metric] ?? 0)
      if (delta !== 0) return delta
      return String(left.name).localeCompare(String(right.name))
    })
  }

  #measurementDatasets(lots) {
    const metric = this.#currentMetric()
    const line = this.originalDatasets?.[0]
    const style = this.#styleFrom(line)
    if (!style.backgroundColor && style.borderColor) style.backgroundColor = style.borderColor

    return [{
      label: line?.label || metric,
      data: lots.map((lot) => lot[metric] ?? 0),
      type: "bar",
      ...style,
    }]
  }

  #presenceDatasets(lots) {
    const bar = this.#originalBarDataset()
    return [{
      label: this.presenceLabelValue,
      data: lots.map((lot) => lot.presence),
      ...this.#styleFrom(bar, {backgroundColor: this.presenceBarColorValue}),
    }]
  }

  #distributionDatasets(lots) {
    return this.#distributionChoiceNames(lots).map((choice) => ({
      label: choice,
      data: lots.map((lot) => lot.choices?.[choice] ?? 0),
      ...this.#styleFrom(this.#originalDatasetFor(choice)),
    }))
  }

  #distributionChoiceNames(lots) {
    const fromOriginal = (this.originalDatasets || [])
      .filter((dataset) => dataset.type !== "line")
      .map((dataset) => dataset.label)
      .filter((name) => lots.some((lot) => Object.prototype.hasOwnProperty.call(lot.choices || {}, name)))

    const extra = []
    lots.forEach((lot) => {
      Object.keys(lot.choices || {}).forEach((choice) => {
        if (!fromOriginal.includes(choice) && !extra.includes(choice)) extra.push(choice)
      })
    })
    return fromOriginal.concat(extra)
  }

  #originalBarDataset() {
    return (this.originalDatasets || []).find((dataset) => dataset.type !== "line")
      || this.originalDatasets?.[0]
  }

  #originalDatasetFor(label) {
    return (this.originalDatasets || []).find((dataset) => dataset.label === label)
  }

  #styleFrom(dataset, fallback = {}) {
    const style = {...fallback}
    if (!dataset) return style

    const keys = ["backgroundColor", "borderColor", "hoverBackgroundColor", "hoverBorderColor", "borderWidth"]
    keys.forEach((key) => {
      const value = dataset[key]
      if (value == null || typeof value === "function") return
      style[key] = Array.isArray(value) ? value[0] : value
    })
    return style
  }

  #showFocusChrome(label) {
    this.element.dataset.focused = "true"
    if (this.hasBackButtonTarget) this.backButtonTarget.classList.remove("hidden")
    if (this.hasFocusedPeriodTarget) {
      this.focusedPeriodTarget.textContent = ` · ${label}`
      this.focusedPeriodTarget.classList.remove("hidden")
    }
  }

  #hideFocusChrome() {
    delete this.element.dataset.focused
    if (this.hasBackButtonTarget) this.backButtonTarget.classList.add("hidden")
    if (this.hasFocusedPeriodTarget) {
      this.focusedPeriodTarget.textContent = ""
      this.focusedPeriodTarget.classList.add("hidden")
    }
  }

  #hitTestIntersect() {
    return this.#kindHandlers().hitTestIntersect
  }

  #currentMetric() {
    if (!this.hasSanitationReportsMetricSwitcherOutlet) return "average"

    return this.sanitationReportsMetricSwitcherOutlet.currentMetric()
  }

  #restoreMeasurementPeriodData() {
    const periodData = this.#periodDataForMetric(this.#currentMetric())
    const chartkick = window.Chartkick?.charts?.[this.chartIdValue]
    if (!periodData || !chartkick?.updateData) return false

    chartkick.updateData(periodData)
    return true
  }

  #periodDataForMetric(metric) {
    if (!this.hasSanitationReportsMetricSwitcherOutlet) return null

    return this.sanitationReportsMetricSwitcherOutlet.dataFor(metric)
  }

  #isClickable(label) {
    return Boolean(this.periodsValue[label]?.length)
  }

  #chartJs() {
    const chart = chartJsFor(this.chartIdValue)
    if (!chart?.canvas || !this.element.contains(chart.canvas)) return null
    if (chart !== this.patchedChart) {
      this.#patchTooltipFooter(chart)
      this.patchedChart = chart
    }
    return chart
  }
}
